import { useCallback, useRef } from 'react';
import type { ChatAction } from '../types';
import {
  UPLOAD_SELFIE_VALUE,
  TALK_TO_PRO_VALUE,
  SHADE_OFFER_YES_VALUE,
  SHADE_OFFER_NO_VALUE,
  SHADE_VIRTUAL_MIRROR_VALUE,
  SHADE_MIRROR_YES_VALUE,
  SHADE_MIRROR_NO_VALUE,
  SHADE_MIRROR_ADD_YES_VALUE,
  SHADE_MIRROR_ADD_NO_VALUE,
  ORDER_STATUS_VALUE,
  ORDER_REPORT_ISSUE_VALUE,
  ORDER_REORDER_VALUE,
  ORDER_SUBSCRIBE_VALUE,
  RETURN_EXCHANGE_TRIGGER,
  ORDER_ISSUE_VALUES,
  RETURN_EXCHANGE_REASON_LABELS,
  RETURN_EXCHANGE_REASON_VALUES,
  KLAVIYO_FORM_ID,
  TALK_TO_PRO_LABELS,
  LIVE_AGENT_HANDOFF_VALUE,
  FIND_MY_SHADE_VALUE,
  PRODUCT_QUESTION_VALUE,
  PRODUCT_QUESTION_LABELS,
  LIVE_AGENT_LABELS,
} from '../constants/businessActions';
import { normalizeActionValue } from '../utils/actionFilters';

interface CreateMessageArgs {
  text: string;
  attachments?: { name: string; type: string; size: number; dataUrl?: string }[];
  subject?: string;
  tid?: string;
  sender?: 'user' | 'ai';
  meta?: Record<string, unknown>;
}

interface UseBusinessActionsOptions {
  chatDispatch: (action: ChatAction) => void;
  chatLoading: boolean;
  createMessage: (args: CreateMessageArgs) => Promise<unknown>;
  threadId: string | null;
  createThread:
    | ((args?: {
        subject?: string;
        message?: string;
        meta?: Record<string, unknown>;
        ch?: string;
      }) => Promise<{ threadId: string; raw: unknown }>)
    | null;
  threadCreateEndpoint: string | null | undefined;
  subject: string;
  companyName: string;
  channel: string;
  allowAttachments: boolean;
  inputDisabled: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onHandoffRequest?: (context: {
    threadId?: string | null;
    preChatData?: Record<string, string>;
  }) => void;
  preChatData?: Record<string, string>;
  trackAnalytics?: (action: string, data: Record<string, unknown>) => void;
  onError?: (error: Error) => void;
}

interface UseBusinessActionsReturn {
  handleButtonClick: (value: string) => Promise<void>;
  handleFormSubmit: (formId: string | undefined, data: Record<string, string>) => Promise<void>;
}

export function useBusinessActions({
  chatDispatch,
  chatLoading,
  createMessage,
  threadId,
  createThread,
  threadCreateEndpoint,
  subject,
  companyName,
  channel,
  allowAttachments,
  inputDisabled,
  fileInputRef,
  onHandoffRequest,
  preChatData,
  trackAnalytics,
  onError,
}: UseBusinessActionsOptions): UseBusinessActionsReturn {
  const actionInFlightRef = useRef(false);

  const ensureThreadForAction = useCallback(async () => {
    if (threadId) return threadId;
    if (!createThread) return null;
    try {
      const created = await createThread({
        subject: subject || (companyName ? `New message from ${companyName}` : 'New message'),
        ch: channel,
      });
      return created?.threadId || null;
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }, [threadId, createThread, subject, companyName, channel, onError]);

  const safeCreateMessage = useCallback(
    async (args: CreateMessageArgs) => {
      chatDispatch({ type: 'SEND_START' });
      try {
        await createMessage(args);
        chatDispatch({ type: 'SEND_SUCCESS' });
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        const userMessage =
          errorObj.message && errorObj.message !== 'Failed to fetch'
            ? `Failed to send: ${errorObj.message}`
            : 'Failed to send. Please try again.';
        chatDispatch({ type: 'SEND_ERROR', error: userMessage });
        onError?.(errorObj);
      }
    },
    [createMessage, chatDispatch, onError]
  );

  const handleButtonClick = useCallback(
    async (value: string) => {
      if (!value) return;
      if (chatLoading || actionInFlightRef.current) return;
      actionInFlightRef.current = true;
      try {
        const [rawValue, ...rest] = value.split('|');
        const baseValue = rawValue.trim();
        const normalizedValue = normalizeActionValue(baseValue);
        const payload = rest.join('|').trim();
        const normalized = normalizedValue;
        const isReturnExchangeLabel = [
          'return / exchange',
          'return/exchange',
          'return exchange',
          'return or exchange',
        ].includes(normalized);
        const isOrderStatusLabel = normalized === 'order status';
        const isFindMyShadeLabel =
          baseValue === FIND_MY_SHADE_VALUE ||
          normalized === 'find my shade' ||
          normalized === 'findmy shade';
        const isProductQuestionLabel =
          baseValue === PRODUCT_QUESTION_VALUE || PRODUCT_QUESTION_LABELS.has(normalized);
        const isLiveAgentLabel =
          baseValue === LIVE_AGENT_HANDOFF_VALUE || LIVE_AGENT_LABELS.has(normalized);
        const metaExtras = payload ? { orderId: payload } : undefined;
        trackAnalytics?.('widget_action_click', {
          action: 'quick_action',
          value: baseValue,
          label: normalized,
        });
        if (baseValue === UPLOAD_SELFIE_VALUE) {
          if (allowAttachments && !inputDisabled) {
            fileInputRef.current?.click();
          } else {
            chatDispatch({ type: 'SET_INPUT', input: 'I want to upload a selfie.' });
          }
          return;
        }
        if (baseValue === TALK_TO_PRO_VALUE || TALK_TO_PRO_LABELS.has(normalized)) {
          if (inputDisabled) {
            chatDispatch({
              type: 'SEND_ERROR',
              error: 'Please complete the required steps before sending.',
            });
            return;
          }
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'Talk to a pro about shade matching.',
            meta: { intent: 'shade_pro' },
          });
          return;
        }
        if (baseValue === FIND_MY_SHADE_VALUE || isFindMyShadeLabel) {
          if (inputDisabled) {
            chatDispatch({
              type: 'SEND_ERROR',
              error: 'Please complete the required steps before sending.',
            });
            return;
          }
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'Find my shade',
            meta: { intent: 'find_my_shade' },
          });
          return;
        }
        if (baseValue === PRODUCT_QUESTION_VALUE || isProductQuestionLabel) {
          if (inputDisabled) {
            chatDispatch({
              type: 'SEND_ERROR',
              error: 'Please complete the required steps before sending.',
            });
            return;
          }
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'Product question',
            meta: { intent: 'product_question' },
          });
          return;
        }
        if (baseValue === SHADE_VIRTUAL_MIRROR_VALUE) {
          if (inputDisabled) {
            chatDispatch({
              type: 'SEND_ERROR',
              error: 'Please complete the required steps before sending.',
            });
            return;
          }
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'Virtual Mirror',
            meta: { intent: 'shade_virtual_mirror', silent: true },
          });
          return;
        }
        if (baseValue === SHADE_MIRROR_YES_VALUE || baseValue === SHADE_MIRROR_NO_VALUE) {
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: baseValue === SHADE_MIRROR_YES_VALUE ? 'Yes' : 'No',
            meta: {
              shadeMirrorAnswer: baseValue === SHADE_MIRROR_YES_VALUE ? 'yes' : 'no',
              silent: true,
            },
          });
          return;
        }
        if (baseValue === SHADE_MIRROR_ADD_YES_VALUE || baseValue === SHADE_MIRROR_ADD_NO_VALUE) {
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: baseValue === SHADE_MIRROR_ADD_YES_VALUE ? 'Yes' : 'No',
            meta: {
              shadeMirrorAddToCart: baseValue === SHADE_MIRROR_ADD_YES_VALUE ? 'yes' : 'no',
              silent: true,
            },
          });
          return;
        }
        if (baseValue === RETURN_EXCHANGE_TRIGGER || isReturnExchangeLabel) {
          if (inputDisabled) {
            chatDispatch({
              type: 'SEND_ERROR',
              error: 'Please complete the required steps before sending.',
            });
            return;
          }
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'I want to start a return or exchange.',
            meta: { intent: 'return_exchange', ...(metaExtras || {}) },
          });
          return;
        }
        if (baseValue === ORDER_STATUS_VALUE || isOrderStatusLabel) {
          if (inputDisabled) {
            chatDispatch({
              type: 'SEND_ERROR',
              error: 'Please complete the required steps before sending.',
            });
            return;
          }
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'Order Status',
            meta: { intent: 'order_status' },
          });
          return;
        }
        if (baseValue === ORDER_REPORT_ISSUE_VALUE) {
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'Report an issue with my order.',
            meta: { intent: 'order_report_issue', ...(metaExtras || {}) },
          });
          return;
        }
        if (baseValue === ORDER_REORDER_VALUE) {
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'I want to reorder.',
            meta: { intent: 'order_reorder', ...(metaExtras || {}) },
          });
          return;
        }
        if (baseValue === ORDER_SUBSCRIBE_VALUE) {
          trackAnalytics?.('widget_action_filtered', {
            action: baseValue,
            reason: 'disabled',
          });
          return;
        }
        if (ORDER_ISSUE_VALUES.has(baseValue)) {
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: 'I have an issue with my order.',
            meta: { orderIssue: baseValue, ...(metaExtras || {}) },
          });
          return;
        }
        const isReturnExchangeReason =
          (baseValue.startsWith('__return_exchange_') && baseValue !== RETURN_EXCHANGE_TRIGGER) ||
          RETURN_EXCHANGE_REASON_VALUES.has(baseValue);
        if (isReturnExchangeReason) {
          if (threadCreateEndpoint) await ensureThreadForAction();
          await safeCreateMessage({
            text: RETURN_EXCHANGE_REASON_LABELS[baseValue] || 'Return / exchange option selected.',
            meta: { returnExchangeReason: baseValue, silent: true, ...(metaExtras || {}) },
          });
          return;
        }
        if (baseValue === SHADE_OFFER_YES_VALUE) {
          const message = 'Yes, I want 15% off.';
          await safeCreateMessage({ text: message });
          return;
        }
        if (baseValue === SHADE_OFFER_NO_VALUE) {
          const message = 'No thanks.';
          await safeCreateMessage({ text: message });
          return;
        }
        if (isLiveAgentLabel) {
          if (inputDisabled) {
            chatDispatch({
              type: 'SEND_ERROR',
              error: 'Please complete the required steps before sending.',
            });
            return;
          }
          const resolvedThreadId = threadCreateEndpoint ? await ensureThreadForAction() : threadId;
          onHandoffRequest?.({ threadId: resolvedThreadId, preChatData });
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('rw:handoff', { detail: { trigger: 'button' } }));
          }
          await safeCreateMessage({
            text: 'I would like to speak with a live agent.',
            meta: { intent: 'live_agent_handoff' },
          });
          return;
        }
        await safeCreateMessage({
          text: baseValue,
          meta: { intent: 'button_click', source: 'quick_action' },
        });
      } finally {
        actionInFlightRef.current = false;
      }
    },
    [
      chatLoading,
      chatDispatch,
      allowAttachments,
      inputDisabled,
      fileInputRef,
      onHandoffRequest,
      preChatData,
      ensureThreadForAction,
      safeCreateMessage,
      threadCreateEndpoint,
      threadId,
      trackAnalytics,
    ]
  );

  const handleFormSubmit = useCallback(
    async (formId: string | undefined, data: Record<string, string>) => {
      if (formId !== KLAVIYO_FORM_ID) return;
      const email = (data.email || '').trim();
      const phone = (data.phone || '').trim();
      if (!email) {
        chatDispatch({ type: 'SEND_ERROR', error: 'Please enter an email address.' });
        return;
      }
      try {
        await createMessage({
          text: 'Sign me up for 15% off.',
          meta: {
            klaviyo: {
              email,
              phone: phone || undefined,
              source: 'shade_match_offer',
            },
          },
        });
      } catch (err) {
        chatDispatch({ type: 'SEND_ERROR', error: 'Signup failed. Please try again.' });
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    },
    [createMessage, chatDispatch, onError]
  );

  return { handleButtonClick, handleFormSubmit };
}
